<template>
	<div align-center class="manager-school-header">
		<template v-if="showSchoolPicker">
			<span class="header-season" v-if="!hasMultipleSeasons">{{ seasonName }}</span>
			<span class="header-season" v-if="season && hasMultipleSeasons">{{ i18n('management.seasonHeader') }}&nbsp;</span>
			<manager-season-picker :seasons="seasons" :season="season" @seasonChanged="seasonChanged" v-if="season && hasMultipleSeasons" ref="seasonPicker" />
			<span class="header-season-pointer" v-if="season">
				&nbsp;<i class="right chevron icon" />
			</span>
			<template v-if="showStudioHeader">
				<manager-studio-picker :schools="allSchools" @studioChanged="studioChanged" />
				<span class="header-season-pointer" v-if="season">
					&nbsp;<i class="right chevron icon" />
				</span>
			</template>
			<manager-school-picker :schools="schools" :loading="loading" @schoolChanged="schoolChanged" v-if="schools.length < 1000" />
			<manager-school-search :schools="schools" :active-school="school" :loading="loading" @schoolChanged="schoolChanged" v-else />
		</template>
		<div class="ui error message" v-if="failed">Failed to load schools</div>
		<a class="ui primary button go-to-school-button" :class="goToSchoolClass" v-if="canViewBook && !!goToSchoolButton && goToSchoolButton != 'false'" :href="goToSchoolUrl" @click="clickedGoToSchool"><i class="book icon"></i>{{ goToSchoolButton }}</a>
	</div>
</template>

<script>
import ManagerSchoolPicker from './ManagerSchoolPicker.vue';
import ManagerSchoolSearch from './ManagerSchoolSearch.vue';
import ManagerSeasonPicker from './ManagerSeasonPicker.vue';
import ManagerStudioPicker from './ManagerStudioPicker.vue';
import localforage from 'localforage';

export default {
	props: {
		plicSlug: null,
		goToSchoolButton: {
			default: 'Go to School'
		},
		getGoToSchoolCallback: null,
		userPermissions: null,
		noProjectsWarning: null,
		orgRole: null
	},
	data: function() {
		return {
			season: null,
			seasons: null,
			schools: [],
			school: null,
			allSchools: [],
			createdDefaultSeason: false,
			loading: false,
			failed: false,
			loadingSeasons: false,
			currentSchoolsLoad: null,
			filterStudio: null,
			defaultSeason: null
		};
	},
	computed: {
		canViewBook: function() {
			return this.userPermissions.allowViewing && this.school;
		},
		goToSchoolUrl: function() {
			if(this.getGoToSchoolCallback && this.getGoToSchoolCallback(this.school)) {
				return null;
			} else {
				return '/Composer.php?jobId=' + this.school.jobId;
			}
		},
		hasMultipleSeasons: function() {
			return this.seasons && this.seasons.length > 1;
		},
		seasonName: function() {
			if(this.season) {
				return 'Season: ' + this.season.name;
			} else {
				return 'No season';
			}
		},
		showSchoolPicker: function() {
			return (this.userPermissions.manageCustomers || this.schools.length > 1) && !this.failed;
		},
		goToSchoolClass: function() {
			return {
				'manage-customers': this.userPermissions.manageCustomers
			};
		},
		showStudioHeader: function() {
			return this.orgRole === 'lab_role';
		}
	},
	watch: {
		season: function(value) {
			$.currentActiveSeason = value;
		},
		allSchools: function(newValue, oldValue) {
			if(newValue === oldValue) {
				return;
			}
			$.allSchoolsIncludingFiltered = newValue;

			// If this loads before getUserDetails finished, filter will be wrong
			if(this.plicSlug) {
				this.updateSchoolsList();
			}
		},
		plicSlug: function(value) {
			if(this.allSchools.length || !this.loading) {
				this.updateSchoolsList();
			}

			if(!this.loadingSeasons && this.userPermissions.manageCustomers && this.userPermissions.viewSeasons) {
				this.loadSeasons();
			}
		}
	},
	methods: {
		clickedGoToSchool: function() {
			if(!this.getGoToSchoolCallback) {
				return;
			}
			
			let goToSchoolCallback = this.getGoToSchoolCallback(this.school);
			if(goToSchoolCallback) {
				goToSchoolCallback(this.school);
			}
		},
		loadSeasons: function() {
			this.loadingSeasons = true;
			var me = this;
			$.plicAPI({
				method: 'organizations/' + $.PlicOrgId + '/seasons',
				success: function(data) {
					me.seasons = data.seasons;
					me.checkForDefaultSeason();
				}
			});
		},
		checkForDefaultSeason: function() {
			var defaultSeasonId = $.getGETParams().seasonId;
			if(!defaultSeasonId || (this.season && this.season.id === defaultSeasonId)) {
				return;
			}

			var defaultSeason = this.seasons.find(function(season) {
				return season.id === defaultSeasonId;
			});
			// Only change season if the season param is not the current active season
			if(defaultSeason && defaultSeason !== this.seasons[0] && !$.currentSetSeason) {
				this.seasonChanged(defaultSeason);
			}
		},
		loadSchools: function(season) {
			this.loading = true;

			$.currentSetSeason = season;
			// Only want to update display immediately if we are explicitely loading a specific season
			if(season) {
				this.season = season;
			}

			if(this.currentSchoolsLoad) {
				this.currentSchoolsLoad.ignoreNextError = true;
				this.currentSchoolsLoad.abort();
				this.currentSchoolsLoad = null;
			}

			var me = this;
			var defaultSeason = null;
			var loadedSchools = [];
			var chain = new $.ExecutionChain((stats) => {
				if(stats.errors) {
					return;
				}
				if(!season) {
					me.season = defaultSeason;

					if (me.season) {
						// Check if we should make a new season automatically
						var today = moment();
						var end = moment(defaultSeason.period_end);

						// If we are in the next year OR still in the current year, but past last season and in the new season (August)
						if(end.year() < today.year() || (end.year() === today.year() && end < today && today.month() >= 7)) {
							me.createDefaultSeason(true);
						}
					} else if(defaultSeason === null) {
						// Make sure we only create season when null, not undefined
						me.createDefaultSeason();
					}
				}

				me.allSchools = loadedSchools;
				me.loading = false;
				me.currentSchoolsLoad = null;

				if(defaultSeason) {
					let seasonId = season ? season.id : defaultSeason.id;
					localforage.setItem(`season-${seasonId}-projects`, {
						schools: loadedSchools,
						season: season ?? defaultSeason,
						defaultSeason: defaultSeason
					}).catch(e => {
						console.warn('Error whilte setting cache: ', e);
					});

					// If we have a get param for season and this finishes before seasons, need to not change so it can re-load the correct season later
					if($.getGETParams().seasonId && $.getGETParams().seasonId !== seasonId && this.seasons) {
						history.replaceState(null, null, window.location.href.replace(`seasonId=${$.getGETParams().seasonId}`, `seasonId=${seasonId}`));
						$.getGETParams().seasonId = seasonId;
					}
				}

				// If we pass in a param seasonId=activeSeasonId we want to have it act like the default of no season set
				// Also switching back to main season should not have it so we can re-load last selected project
				if(this.defaultSeason && season && season.id === this.defaultSeason.id) {
					$.currentSetSeason = null;
				}
			});
			this.currentSchoolsLoad = chain;

			chain.add({
				function: async ({ onComplete }) => {
					let cachedData;
					if($.getGETParams().seasonId && $.getGETParams().cache) {
						// NOTE: We are turning off Management cache loading for now - if we change a setting for the project (ie: submit from Composer) then we will be loading a stale cache version
						/*try {
							cachedData = await localforage.getItem(`season-${$.getGETParams().seasonId}-projects`);
						} catch(e) {
							console.warn('Failed to decode cached schools', e);
						}*/
						
						// Want user to be able to refresh current page and load schools again
						delete $.getGETParams().cache;
						history.replaceState(null, null, window.location.href.replace('&cache=true', ''));
					}

					if(cachedData) {
						loadedSchools = cachedData.schools;
						defaultSeason = cachedData.defaultSeason;

						// This means this probably executed before done loading seasons from PLIC and we are loading a non-default season from query param
						if(!season && cachedData.season.id !== defaultSeason.id) {
							this.season = season = cachedData.season;
							$.currentSetSeason = season;
							this.defaultSeason = defaultSeason;
						}
					} else {
						const PROJECT_PAGE_COUNT = 1000;
						chain.add({
							url: 'ajax/getSchools.php',
							type: 'POST',
							data: {
								seasonId: (season && (!this.defaultSeason || season.id !== this.defaultSeason.id)) ? season.id : null,
								page: 1,
								pageLimit: PROJECT_PAGE_COUNT
							},
							timeout: 30000,
							success: function(data) {
								defaultSeason = data.season;
								if(!season) {
									me.defaultSeason = data.season;
								}
								$.merge(loadedSchools, data.schools);

								if(data.page) {
									for(var i = 1; i < data.page.total_pages; i++) {
										chain.add({
											url: 'ajax/getSchools.php',
											type: 'POST',
											data: {
												seasonId: season ? season.id : null,
												page: i + 1,
												pageLimit: PROJECT_PAGE_COUNT
											},
											timeout: 30000,
											success: function(data) {
												$.merge(loadedSchools, data.schools);
											},
											error: function(e) {
												if(me.currentSchoolsLoad && me.currentSchoolsLoad.ignoreNextError) {
													return;
												}
							
												me.failed = true;
												me.loading = false;
												me.currentSchoolsLoad = null;
												
												if(!$.disableAjaxErrorReporting) {
													if(e.status === 0) {
														$.Alert('Error', 'Failed to load school information.  Try again later.');
													} else {
														$.fireErrorReport('Failed to load school information.  Try again later.', 'Failed to load manager', 'Failed to load getSchools.php', {
															e: e
														});
													}
												}
											}
										});
									}
								}
							},
							error: function(e) {
								if(me.currentSchoolsLoad && me.currentSchoolsLoad.ignoreNextError) {
									return;
								}

								me.failed = true;
								me.loading = false;
								me.currentSchoolsLoad = null;
								
								if(!$.disableAjaxErrorReporting) {
									if(e.status === 0) {
										$.Alert('Error', 'Failed to load school information.  Try again later.');
									} else {
										$.fireErrorReport('Failed to load school information.  Try again later.', 'Failed to load manager', 'Failed to load getSchools.php', {
											e: e
										});
									}
								}
							}
						});
					}

					onComplete();
				}
			});
			
			chain.done();
		},
		createDefaultSeason: function(override) {
			// Only allow on top level orgs
			if($.PlicOrgId != $.UserPlicOrgId) {
				return;
			} else if(!$.isManager) {
				return;
			} else if($.currentActiveSeason && override !== true) {
				return;
			} else if(this.createdDefaultSeason) {
				// Make extra sure we only call this once
				return;
			}

			var year = moment().year();
			var month = moment().month();
			if(month <= 6) {
				year--;
			}

			var seasonPost = {
				name: (year + ' - ' + (year + 1)),
				period_start: moment(year + '-08-01').toISOString(),
				period_end: moment((year + 1) + '-06-30').toISOString(),
				organization_id: $.PlicOrgId
			};

			var me = this;
			$.plicAPI({
				method: 'seasons',
				type: 'post',
				params: {
					season: seasonPost
				},
				success: function(data) {
					me.season = data.season;
					if(me.seasons) {
						me.seasons.unshift(data.season);
					}

					if(override) {
						if(me.$refs.seasonPicker) {
							$(me.$refs.seasonPicker.$el).dropdown('set text', me.season.name);
						}
						me.loadSchools();
						$.Alert('Warning', 'A new season has been automatically created.  You can access your old season from Studio/Lab Settings -> Seasons.');
					}
				}
			});
			this.createdDefaultSeason = true;
		},
		updateSchoolsList: function(forceRefresh) {
			var schools = this.allSchools.concat();
			schools.caseInsensitiveSort('jobName');
			if(this.filterStudio) {
				var filterStudio = this.filterStudio;
				schools = schools.filter(function(job) {
					return job.parentOrganizationId == filterStudio || job.organizationId == filterStudio;
				});
			}
			var allSchoolsCount = schools.length;

			var includeAllAppProjects = $.getStudioSetting('hideAllAppProjects', false) ? false : true;

			var plicSlug = this.plicSlug;
			var filteredSchools = schools.filter(function(school) {
				if(school.plicAppWhitelist) {
					return school.plicAppWhitelist.indexOf(plicSlug) != -1;
				} else {
					return includeAllAppProjects;
				}
			});
			if($.filterSchoolsByPlicSlug) {
				schools = filteredSchools;
			}

			this.schools = schools;
			$.filteredHiddenSchoolCount = allSchoolsCount - filteredSchools.length;

			$.schoolListManager.changeActive(schools);
			window.updateFilterHiddenOrgsField();

			if(!this.showSchoolPicker || forceRefresh || schools.length === 0) {
				if(!this.school || schools.indexOfMatch(this.school, 'jobId') === -1) {
					this.schoolChanged(schools[0] || null);
				}
			}

			// When deleting/removing projects want to make sure we aren't keeping a bad cache record
			if(forceRefresh) {
				localforage.clear();
			}
		},
		schoolChanged: function(school) {
			var changed = !this.school || !school || this.school.jobId != school.jobId;
			$.currentLoadedSchool = this.school = school;

			if($.organizationDataTable && changed && school) {
				$.organizationDataTable.fnHighlightRow(school);
			}
			
			$.schoolChangeManager.changeActive(school);
		},
		studioChanged: function(studio) {
			this.filterStudio = $.currentActiveStudio = studio;
			$.currentActiveStudio = studio ? parseInt(studio) : null;
			this.updateSchoolsList();
		},

		registerSchoolListManager: function() {
			var me = this;
			$.schoolListManager = new $.ActiveTabManager({
				globalAccess: 'allLoadedSchools'
			});

			$.schoolChangeManager = new $.ActiveTabManager({
				globalAccess: 'currentLoadedSchool',
				noProjectsMessage: this.noProjectsWarning
			});

			window.registerForSchoolChange = function(settings) {
				$.schoolChangeManager.register(settings);

				if(!me.loading && !me.error && !me.school) {
					$.schoolChangeManager.changeActiveForListener(null, settings);
				}
			};

			window.registerSubTabForSchoolChange = function(tab) {
				var startSchool = $.currentLoadedSchool;
				window.registerForSchoolChange({
					tab: tab,
					onSchoolChangeHandler: function(school) {
						if(startSchool != school) {
							window.setTimeout(function() {
								window.returnToMainTab();
							}, 1);
						}
					}
				});
			};

			window.updateFilterHiddenOrgsField = function() {
				var filterHiddenOrgsField = $('#filterHiddenOrgsField');
				if(filterHiddenOrgsField.length) {
					if($.filteredHiddenSchoolCount) {
						filterHiddenOrgsField.show().children('label').text('Show ' + $.filteredHiddenSchoolCount + ' Hidden');
					} else {
						filterHiddenOrgsField.hide();
					}
				}
			};

			window.updateSchoolsList = function() {
				me.updateSchoolsList();
			};

			window.addSchoolToDropdown = function(school, setAsDefault) {
				me.allSchools.push(school);
				me.updateSchoolsList(true);
				if(setAsDefault !== false && window.selectSchoolInDropdown) {
					Vue.nextTick(function() {
						Vue.nextTick(function() {
							window.selectSchoolInDropdown(school);
						});
					});
				}
			};

			window.removeSchoolFromDropdown = function(school) {
				me.allSchools.removeItem(school);
				me.updateSchoolsList(true);
			};
			window.updateSchoolInDropdown = function() {
				// Vue already handles it
			};
			window.loadSchools = function(season) {
				me.loadSchools(season);
			}
		},
		seasonChanged: function(season) {
			this.season = season;
			this.loadSchools(season);
		}
	},
	mounted: function() {
		this.registerSchoolListManager();
		this.loadSchools();
	},
	components: {
		ManagerSchoolPicker,
		ManagerSchoolSearch,
		ManagerSeasonPicker,
		ManagerStudioPicker
	}
};
</script>

<style>
.manager-school-header {
	z-index: 10000;
	flex: 1;

	display: flex;
	align-items: center;
	justify-content: center;
}

@media only screen and (max-width: 991px) {
	.manager-school-header .header-season, .manager-school-header .header-season-pointer {
		display: none;
	}

	.manager-school-header .ui.search.dropdown.manager-school-picker .menu {
		max-height: 20rem;
	}
}


@media only screen and (max-width: 600px) {
	.manager-school-header .go-to-school-button.manage-customers {
		display: none;
	}
}

.manager-school-header .manager-school-picker > .text .ui.header {
	color: white;
}
.manager-school-header .manager-school-picker > .text .sub.header {
	color: rgba(255, 255, 255, 0.9);
}

.manager-school-header .ui.button.go-to-school-button {
	white-space: nowrap;
	margin-left: 0.5em;
}

/* Matching the existing dropdown's result setup */
.manager-school-header .manager-school-picker-search > .results .result .title {
	font-size: 1.07142857em;
}
.manager-school-header .manager-school-picker-search > .results .result .description {
	font-size: 1rem;
	color: rgba(0,0,0,.6);
	line-height: 1.rem;
}
</style>